import React, { useEffect, useState } from 'react';

/**
 * POS 주문 알림 토스트 — OKDC OrderNotificationWindow.xaml (v2.1.50, 시안 3) React 포팅.
 * 상단 녹색 NAVER 리뷰 영역 + 헤더 + 메뉴 + 푸터. 카드 폭 312.
 *
 * 모든 클래스명에 ot- prefix — 다른 컴포넌트(.time 등) 와 충돌 방지.
 *
 * Props:
 *   - tableName: string ("101")
 *   - isNewOrder: boolean (true: 노란 "새 주문", false: 파란 "추가 주문")
 *   - menuItems: [{ name, quantity, options: [{name}], isReviewBenefit?: bool }]
 *   - totalPrice: number
 *   - reviewBenefit: string | null
 *   - onClose?: () => void
 */
const OrderToast = ({
  tableName = '101',
  isNewOrder = true,
  menuItems = [],
  totalPrice = 0,
  reviewBenefit = null,
  reviewChannel = 'naver',
  onClose,
}) => {
  const isKakao = reviewChannel === 'kakao';
  const [createdAt] = useState(() => Date.now());
  const [timeText, setTimeText] = useState('방금 전');

  const displayItems = menuItems.filter((m) => !m.isReviewBenefit);
  const totalQty = displayItems.reduce((sum, m) => sum + (m.quantity || 0), 0);

  useEffect(() => {
    const tick = () => {
      const elapsed = (Date.now() - createdAt) / 1000;
      if (elapsed < 60) setTimeText('방금 전');
      else if (elapsed < 3600) setTimeText(`${Math.floor(elapsed / 60)}분 전`);
      else setTimeText(`${Math.floor(elapsed / 3600)}시간 전`);
    };
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [createdAt]);

  return (
    <div className="ot-wrap" onClick={onClose}>
      <div className="ot-enter">
        <div className="ot-shake">
          <div className="ot-card">
            {/* 0. 리뷰 요청 영역 (네이버=녹색/영수증 · 카카오=노랑/카카오 로고) */}
            {reviewBenefit && (
              <div className="ot-review-section" style={isKakao ? { background: '#FEE500' } : undefined}>
                <div className="ot-review-icon" aria-hidden>
                  {isKakao ? (
                    <div style={{ width: 34, height: 34, borderRadius: 8, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <svg viewBox="0 0 450 450" width="26" height="26" xmlns="http://www.w3.org/2000/svg">
                        <path fill="#fae000" transform="matrix(1.03079 0 0 1.03079 -163.262 -511.396)" d="M220.265 496.122c-34.176 0-61.88 27.703-61.88 61.866v312.815c0 34.176 27.704 61.879 61.88 61.879h312.814c34.176 0 61.867-27.703 61.867-61.88V557.989c0-34.163-27.69-61.866-61.867-61.866z" />
                        <path fill="#007dfc" transform="matrix(1.03079 0 0 1.03079 -163.262 -511.396)" d="M378.298 552.754c-63.196-.038-114.244 50.744-114.333 113.586-.05 28.5 11.666 52.834 29.312 73.596 32.187 37.849 60.359 77.712 75.736 125.556 1.381 4.307 3.459 9.222 9.4 9.07 6.46-.165 8.195-5.662 9.728-10.311 14.428-43.778 38.546-81.59 69.01-115.942 18.102-20.407 32.63-43.93 35.063-71.57 5.94-67.338-47.312-124.91-113.916-123.985m38.863 114.662c-.152 20.8-18.456 38.99-39.04 38.8-20.774-.19-38.8-18.444-38.723-39.167.076-20.71 18.392-38.875 39.09-38.774 20.673.089 38.825 18.469 38.673 39.141" />
                      </svg>
                    </div>
                  ) : (
                    <svg viewBox="0 0 24 24" width="38" height="38" fill="none">
                      <path
                        d="M5,21 V5 A2,2 0 0 1 7,3 H17 A2,2 0 0 1 19,5 V21 L16,19 L14,21 L12,19 L10,21 L8,19 L5,21 M9,7 H15 M9,11 H15 M9,15 H12"
                        stroke="#FFFFFF"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <div className="ot-review-text">
                  <div className="ot-review-title" style={isKakao ? { color: '#191919' } : undefined}>
                    {isKakao ? (
                      <span className="ot-review-title-suffix" style={{ color: '#191919' }}>카카오 리뷰 서비스</span>
                    ) : (
                      <>
                        <img className="ot-review-logo" src="/naver_logo_white.png" alt="NAVER" />
                        <span className="ot-review-title-suffix">리뷰 영수증을 가져다주세요!</span>
                      </>
                    )}
                  </div>
                  <div className="ot-review-service" style={isKakao ? { color: 'rgba(25,25,25,0.72)' } : undefined}>{reviewBenefit}</div>
                </div>
              </div>
            )}

            {/* 1. 헤더 */}
            <div className="ot-header">
              <div className="ot-header-top">
                <span className={`ot-badge ${isNewOrder ? 'ot-badge-new' : 'ot-badge-add'}`}>
                  {isNewOrder ? '새 주문' : '추가 주문'}
                </span>
                <span className="ot-time">{timeText}</span>
              </div>
              <div className="ot-table-row">
                <span className="ot-table-num">{tableName}</span>
                <span className="ot-table-suffix"> 테이블</span>
              </div>
            </div>

            {/* 2. 메뉴 리스트 */}
            <div className="ot-menu-list">
              {displayItems.map((item, i) => {
                const optText = (item.options || [])
                  .filter((o) => o && o.name)
                  .map((o) => o.name)
                  .join(', ');
                return (
                  <div key={i} className="ot-menu-row">
                    <div className="ot-menu-left">
                      <div className="ot-menu-name">{item.name}</div>
                      {optText && (
                        <div className="ot-opt-pill"><span>{optText}</span></div>
                      )}
                    </div>
                    <div className="ot-menu-qty">
                      <span className="ot-qty-num" style={{ color: isNewOrder ? '#F8CA2E' : '#3D8BFF' }}>{item.quantity}</span>
                      <span className="ot-qty-suffix"> 개</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 3. 푸터 */}
            <div className="ot-footer">
              <span className="ot-count">메뉴 {totalQty}개</span>
              <span className="ot-price">{totalPrice.toLocaleString()}원</span>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes otEnter {
          0%   { opacity: 0; transform: translateY(26px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes otShake {
          0%    { transform: rotate(0deg); }
          18%   { transform: rotate(-3deg); }
          42%   { transform: rotate(3deg); }
          66%   { transform: rotate(-1.8deg); }
          85%   { transform: rotate(1deg); }
          100%  { transform: rotate(0deg); }
        }

        .ot-wrap {
          /* OKDC Window Width 408 + Margin 34 → 카드 폭 340 */
          width: 408px;
          padding: 0 34px;
          box-sizing: border-box;
          pointer-events: auto;
          cursor: pointer;
        }
        .ot-enter {
          animation: otEnter 340ms cubic-bezier(0.2, 0.8, 0.4, 1) both;
        }
        .ot-shake {
          animation: otShake 360ms linear both;
          transform-origin: 50% 50%;
          will-change: transform;
        }
        .ot-card {
          width: 340px;
          background: #232323;
          border-radius: 18px;
          /* CardShadow: blur 34, depth 12, opacity .35 */
          box-shadow: 0 12px 34px rgba(0, 0, 0, 0.35);
          color: #FFFFFF;
          font-family: 'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
          overflow: hidden;
        }

        /* NAVER REVIEW SECTION (top) */
        .ot-review-section {
          display: flex;
          background: #03C75A;
          padding: 17px 20px 18px;
        }
        .ot-review-icon {
          flex-shrink: 0;
          display: flex;
          align-items: center;
        }
        .ot-review-text {
          margin-left: 11px;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }
        .ot-review-title {
          display: flex;
          align-items: center;
          line-height: 1;
        }
        .ot-review-logo {
          height: 12px;
          margin-right: 3px;
        }
        .ot-review-title-suffix {
          font-size: 14.5px;
          font-weight: 800;
          color: #FFFFFF;
        }
        .ot-review-service {
          margin-top: 6px;
          font-size: 14px;
          font-weight: 600;
          color: rgba(255,255,255,0.82);
        }

        /* HEADER */
        .ot-header {
          padding: 14px 22px 13px;
        }
        .ot-header-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .ot-badge {
          display: inline-block;
          /* OKDC XAML Padding="12,6,12,4" 매핑 — 좌·상·우·하 → CSS top right bottom left */
          padding: 6px 12px 4px 12px;
          border-radius: 11px;
          font-size: 11.5px;
          font-weight: 700;
        }
        .ot-badge-new {
          background: #FDCD27;
          color: #191919;
        }
        .ot-badge-add {
          background: #4DA8FF;
          color: #FFFFFF;
        }
        .ot-time {
          font-size: 12px;
          font-weight: 500;
          color: #8C8C8C;
        }
        .ot-table-row {
          margin-top: 9px;
          display: flex;
          align-items: baseline;
        }
        .ot-table-num {
          font-size: 38px;
          font-weight: 800;
          color: #FFFFFF;
          letter-spacing: -0.02em;
          line-height: 1;
        }
        .ot-table-suffix {
          font-size: 15px;
          font-weight: 600;
          color: #9A9A9A;
          margin-left: 4px;
        }

        /* MENU LIST */
        .ot-menu-list {
          padding: 0 22px 7px;
        }
        .ot-menu-row {
          display: flex;
          padding: 10px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        .ot-menu-left {
          flex: 1;
        }
        .ot-menu-name {
          font-size: 16px;
          font-weight: 700;
          color: #F2F2F2;
        }
        .ot-opt-pill {
          display: inline-block;
          margin-top: 5px;
          padding: 2px 8px;
          background: rgba(255, 255, 255, 0.07);
          border-radius: 6px;
        }
        .ot-opt-pill span {
          font-size: 11.5px;
          font-weight: 600;
          color: #A8A9AF;
        }
        .ot-menu-qty {
          display: flex;
          align-items: flex-end;
        }
        .ot-qty-num {
          font-size: 18px;
          font-weight: 800;
          color: #FDCD27;
          line-height: 1;
        }
        .ot-qty-suffix {
          font-size: 11.5px;
          font-weight: 600;
          color: #8C8C8C;
          margin-left: 1px;
          margin-bottom: 1px;
        }

        /* FOOTER */
        .ot-footer {
          background: #2B2B2B;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding: 14px 22px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .ot-count {
          font-size: 13px;
          font-weight: 500;
          color: #9A9A9A;
        }
        .ot-price {
          font-size: 20px;
          font-weight: 800;
          color: #FFFFFF;
        }
      `}</style>
    </div>
  );
};

export default OrderToast;
